import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  Outlet,
} from "@tanstack/react-router";
import type { ObjectiveLevel, ObjectiveStatus, ObjectiveType } from "@/types/entities";
import { AppShell } from "@/components/layout/app-shell";
import { DashboardPage } from "@/pages/dashboard";
import { TeamDashboardPage } from "@/pages/dashboard-team";
import { ObjectivesPage } from "@/pages/objectives";
import { ObjectivesPrintPage } from "@/pages/objectives-print";
import { TeamObjectivesPage } from "@/pages/objectives-team";
import { ObjectiveDetailPage } from "@/pages/objective-detail";
import { KeyResultDetailPage } from "@/pages/key-result-detail";
import { ReviewsPage } from "@/pages/reviews";
import { ReviewDetailPage } from "@/pages/review-detail";
import { AlignmentPage } from "@/pages/alignment";
import { SearchPage } from "@/pages/search";
import { AdminUsersPage } from "@/pages/admin-users";
import { AdminTeamsPage } from "@/pages/admin-teams";
import { AdminStrategicPlansPage } from "@/pages/admin-strategic-plans";
import { AdminCollegeStrategyPage } from "@/pages/admin-college-strategy";
import { AdminCoachPromptsPage } from "@/pages/admin-coach-prompts";
import { AdminOkrExamplesPage } from "@/pages/admin-okr-examples";
import { CoachWizardPage } from "@/features/okr-coach/coach-wizard";

export type ObjectivesView = 'cards' | 'table';

export const OBJECTIVES_EMPTY_SEARCH = {
  year: undefined as number | undefined,
  level: undefined as ObjectiveLevel | undefined,
  status: undefined as ObjectiveStatus | undefined,
  type: undefined as ObjectiveType | undefined,
  focus_area_id: undefined as string | undefined,
  owner_id: undefined as string | undefined,
  view: undefined as ObjectivesView | undefined,
};

const validLevel = (v: unknown) =>
  (['department', 'team', 'individual'] as const).includes(v as ObjectiveLevel)
    ? (v as ObjectiveLevel)
    : undefined;
const validStatus = (v: unknown) =>
  (['draft', 'approved', 'in_progress', 'completed', 'killed'] as const).includes(v as ObjectiveStatus)
    ? (v as ObjectiveStatus)
    : undefined;
const validType = (v: unknown) =>
  (['committed', 'aspirational'] as const).includes(v as ObjectiveType)
    ? (v as ObjectiveType)
    : undefined;
export const validView = (v: unknown): ObjectivesView | undefined =>
  v === 'cards' || v === 'table' ? v : undefined;

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardPage,
});

const teamDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/team",
  component: TeamDashboardPage,
});

const objectivesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/objectives",
  component: ObjectivesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    year: typeof search.year === 'number' ? search.year : typeof search.year === 'string' ? (parseInt(search.year, 10) || undefined) : undefined,
    level: validLevel(search.level),
    status: validStatus(search.status),
    type: validType(search.type),
    focus_area_id: typeof search.focus_area_id === 'string' ? search.focus_area_id : undefined,
    owner_id: typeof search.owner_id === 'string' ? search.owner_id : undefined,
    view: validView(search.view),
  }),
});

const teamObjectivesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/objectives/team",
  component: TeamObjectivesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    year: typeof search.year === 'number' ? search.year : typeof search.year === 'string' ? (parseInt(search.year, 10) || undefined) : undefined,
    level: validLevel(search.level),
    status: validStatus(search.status),
    type: validType(search.type),
    focus_area_id: typeof search.focus_area_id === 'string' ? search.focus_area_id : undefined,
    owner_id: typeof search.owner_id === 'string' ? search.owner_id : undefined,
    view: validView(search.view),
  }),
});

const guidedBuilderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/objectives/guided-builder",
  component: CoachWizardPage,
});

const objectivesPrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/objectives/print",
  component: ObjectivesPrintPage,
  validateSearch: (search: Record<string, unknown>) => ({
    year: typeof search.year === 'number' ? search.year : typeof search.year === 'string' ? (parseInt(search.year, 10) || undefined) : undefined,
    level: validLevel(search.level),
    status: validStatus(search.status),
    type: validType(search.type),
    focus_area_id: typeof search.focus_area_id === 'string' ? search.focus_area_id : undefined,
    owner_id: typeof search.owner_id === 'string' ? search.owner_id : undefined,
  }),
});

const objectiveDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/objectives/$id",
  component: ObjectiveDetailPage,
});

const keyResultDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/key-results/$id",
  component: KeyResultDetailPage,
});

const reviewsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reviews",
  component: ReviewsPage,
});

const reviewDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reviews/$id",
  component: ReviewDetailPage,
});

const alignmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/alignment",
  component: AlignmentPage,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/search",
  component: SearchPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/users",
  component: AdminUsersPage,
});

const adminTeamsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/teams",
  component: AdminTeamsPage,
});

const adminStrategicPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/strategic-plans",
  component: AdminStrategicPlansPage,
});

const adminCollegeStrategyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/college-strategy",
  component: AdminCollegeStrategyPage,
});

const adminCoachPromptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/coach-prompts",
  component: AdminCoachPromptsPage,
});

const adminOkrExamplesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/okr-examples",
  component: AdminOkrExamplesPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  teamDashboardRoute,
  objectivesRoute,
  teamObjectivesRoute,
  guidedBuilderRoute,
  objectivesPrintRoute,
  objectiveDetailRoute,
  keyResultDetailRoute,
  reviewsRoute,
  reviewDetailRoute,
  alignmentRoute,
  searchRoute,
  adminUsersRoute,
  adminTeamsRoute,
  adminStrategicPlansRoute,
  adminCollegeStrategyRoute,
  adminCoachPromptsRoute,
  adminOkrExamplesRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
